import { useEffect, useState, useCallback } from "react";
import { debugLog } from "../common/utils/debug";
import { Message, SessionState, AudioState } from "../types";
import { ToolCall } from "../components/shared/ToolsPanel";
import {
  INITIAL_SESSION_STATE,
  INITIAL_AUDIO_STATE,
} from "../common/constants/app.constants";
import { SESSION_STORAGE_KEYS } from "../common/constants/sessionStorage.constants";
import { avatarHealthMonitor } from "../services/avatar-health-monitor.service";

export const useAppState = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionState, setSessionState] = useState<SessionState>(
    INITIAL_SESSION_STATE
  );
  const [audioState, setAudioState] = useState<AudioState>(INITIAL_AUDIO_STATE);
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([]);
  const [isThinking, setIsThinking] = useState(false);

  const addMessage = useCallback((message: Message) => {
    avatarHealthMonitor.updateActivity();
    setMessages((prev) => {
      // Check for duplicate message by ID only (backend is source of truth)
      // Don't check by content - user may say the same thing multiple times (e.g., "No." repeatedly)
      const isDuplicate = prev.some((m) => m.id === message.id);
      if (isDuplicate) {
        debugLog("🚫 [useAppState] Duplicate message ID detected, skipping:", message.id);
        return prev;
      }
      debugLog("✅ [useAppState] Adding new message:", message);
      const newMessages = [...prev, message];
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.MESSAGES,
        JSON.stringify(newMessages)
      );
      return newMessages;
    });
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.MESSAGES);
  }, []);

  const updateLastMessage = useCallback((text: string) => {
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];
      if (lastMessage && lastMessage.role === "assistant") {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + text,
        };
        return updated;
      } else {
        return prev;
      }
    });
  }, []);

  const addToolCall = useCallback((toolCall: ToolCall) => {
    setToolCalls((prev) => [...prev, toolCall]);
  }, []);

  const updateToolCall = useCallback((id: string, update: Partial<ToolCall>) => {
    setToolCalls((prev) =>
      prev.map((tool) => (tool.id === id ? { ...tool, ...update } : tool))
    );
  }, []);

  const resetSession = useCallback(() => {
    setSessionState(INITIAL_SESSION_STATE);
    setAudioState(INITIAL_AUDIO_STATE);
    setToolCalls([]);
  }, []);

  useEffect(() => {
    const messages = sessionStorage.getItem(SESSION_STORAGE_KEYS.MESSAGES);
    if (messages) {
      setMessages(JSON.parse(messages));
    }
  }, []);

  return {
    // State
    messages,
    sessionState,
    audioState,
    toolCalls,
    isThinking,

    // State setters
    setMessages,
    setSessionState,
    setAudioState,
    setToolCalls,
    setIsThinking,

    // Helper functions
    addMessage,
    clearMessages,
    updateLastMessage,
    addToolCall,
    updateToolCall,
    resetSession,
  };
};
